import { OrderDirection, OrderType } from 'store/spot/types'
import {
  AccountId,
  Ether,
  OrderId,
  Quantity,
  SymbolName,
  Timestamp,
  UserId,
} from 'types'
import { ApiV1Response, http } from './http'

export interface TradingFeeRates {
  taker: number // 20, means 0.2% from 20/10000
  maker: number // 10, means 0.1% from 10/10000
  timestamp?: Timestamp
}
export async function getSpotFeeRates<
  T extends ApiV1Response<TradingFeeRates>,
>(): Promise<T> {
  const r = await http.get<T>(`/trading/spots/fee/rate`)
  return r.data
}

export interface CreateOrderBody {
  direction: OrderDirection
  triggerDirection: OrderDirection // Stop order trigger direction
  type: OrderType
  source: 'WEB'
  symbol: string // 'BTC_USDT'
  quantity: Ether // '0.05'
  postOnly: boolean
  hidden: boolean // Iceberg
  price: number // 42000
  triggerOn: number // Stop order trigger price
  fillOrKill: boolean
  immediateOrCancel: boolean
  slotId: number // 12146
  signature: string
  nonce: number
  makerFeeRate: number // 0.0255, means 2.55% from 255/10000, maximum 0.0255
  takerFeeRate: number // 0.0255, means 2.55% from 255/10000, maximum 0.0255
  reduceOnly?: boolean // Perp only
  clientOrderId?: string
}

export interface CreateSpotOrderResponse {
  orderId: number
}

export async function postCreateSpotOrder<
  T extends ApiV1Response<CreateSpotOrderResponse>,
>(accountId: number, data: CreateOrderBody): Promise<T> {
  return http
    .post<T>(`/trading/${accountId}/spots/orders`, data)
    .then((r) => r.data)
}

export enum SpotOrderStatus {
  PENDING = 'PENDING', // Order is submitted to match and pending to be processed (filledAmount = 0).
  STOP_PENDING = 'STOP_PENDING',
  FAILED = 'FAILED', // Order is failed to execute (e.g. market order with no market price).
  FULLY_FILLED = 'FULLY_FILLED', // Limit order is fully filled (filledAmount = amount, do not in order book) or market order is filled.
  PARTIAL_FILLED = 'PARTIAL_FILLED', // Limit order is partially filled (amount > filledAmount > 0) and still in order book as maker.
  PARTIAL_CANCELLED = 'PARTIAL_CANCELLED', // ONLY for limit order / stop limit order: order is partially cancelled and done as final status (amount > filledAmount > 0).
  FULLY_CANCELLED = 'FULLY_CANCELLED', // ONLY for limit order / stop order / stop limit order: order is fully cancelled (filledAmount = 0).
}

export enum Role {
  'MAKER' = 'MAKER',
  'TAKER' = 'TAKER',
}
export interface SpotOpenOrdersData {
  results: SpotOrderRecord[]
}

export interface SpotMatchClearingsData {
  range: string // "202305", date
  hasMore: boolean // false, has more record
  nextOffsetId: number // 0, offsetId(first of next page) for requesting the next page of data
  results: SpotMatchRecord[]
}
export interface SpotMatchRecord {
  id?: number
  orderId: OrderId
  userId: UserId
  accountId: AccountId
  sequenceId: number
  direction: OrderDirection
  type: Role
  matchPrice: number
  matchQuantity: number
  orderStatusAfterClearing: SpotOrderStatus
  orderUnfilledQuantityAfterClearing: number
  feeCurrency: string
  fee: number
  rate: number
  createdAt: Timestamp
  symbol: SymbolName
  txHash: string
}

export interface SpotOrderRecord {
  id: OrderId // 2552832305
  userId: UserId // 10080
  accountId: AccountId // 0
  clientOrderId: string // '@2552832305'
  symbolId: number // 100103
  sequenceId: number // 255283
  type: OrderType // "LIMIT"
  status: SpotOrderStatus // "PENDING"
  direction: OrderDirection // "LONG"
  features: number // 0
  price: number // 40000.0
  fee: number // 0.0
  fillPrice: number // 0.0
  makerFeeRate: number // 0.001
  takerFeeRate: number // 0.002
  createdAt: Timestamp // 1684735960126
  updatedAt: Timestamp // 1684735960126
  chargeQuote?: boolean // true
  quantity: Quantity // 1.00
  unfilledQuantity: Quantity // 1.00
  symbol: SymbolName // "BTC_USDC"
  triggerDirection: OrderDirection // "LONG"
  triggerOn: Ether // "0.0"
}
export async function getSpotOpenOrders<
  T extends ApiV1Response<SpotOpenOrdersData>,
>(accountId: number): Promise<T> {
  const r = await http.get<T>(`/trading/${accountId}/spots/orders/open`)
  return r.data
}

export interface SpotClosedOrdersParams {
  range?: string
  symbol?: SymbolName
  offsetId?: number
  limit?: number
  direction?: string
}
export interface SpotClosedOrdersData {
  range: string // "202305", date
  hasMore: boolean // false, has more record
  nextOffsetId: number // 0, offsetId(first of next page) for requesting the next page of data
  results: SpotOrderRecord[]
}
export async function getSpotClosedOrders<
  T extends ApiV1Response<SpotClosedOrdersData>,
>(accountId: number, params: SpotClosedOrdersParams): Promise<T> {
  const r = await http.get<T>(`/trading/${accountId}/spots/orders/closed`, {
    params: {
      limit: 10,
      range: '',
      symbol: '',
      offsetId: 0,
      ...params,
    },
  })
  return r.data
}

export async function cancelSpotOrdersById<
  T extends ApiV1Response<SpotClosedOrdersData>,
>(accountId: number, orderId: number): Promise<T> {
  const r = await http.post<T>(
    `/trading/${accountId}/spots/orders/${orderId}/cancel`,
  )
  return r.data
}

export interface CancelSpotOrdersBody {
  symbol?: string
  type?: 'LIMIT' | 'STOP'
}
export async function cancelSpotAllOrders<
  T extends ApiV1Response<SpotClosedOrdersData>,
>(accountId: number, data: CancelSpotOrdersBody): Promise<T> {
  const r = await http.post<T>(
    `/trading/${accountId}/spots/orders/cancel`,
    data,
  )
  return r.data
}

export async function getSpotMatchClearings<
  T extends ApiV1Response<SpotMatchClearingsData>,
>(
  accountId: number,
  params: SpotClosedOrdersParams = {
    range: '',
    symbol: '',
    offsetId: 0,
    limit: 10,
  },
): Promise<T> {
  const r = await http.get<T>(`/trading/${accountId}/spots/match/clearings`, {
    params: {
      limit: 10,
      range: '',
      symbol: '',
      offsetId: 0,
      ...params,
    },
  })
  return r.data
}
